import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatAmountWithCode } from '@app/helpers/formatCurrency.helper';
import {
  formatDate,
  formatDateTime,
  formatTime,
} from '@app/helpers/formatDateTime.helper';
import {
  getActionType,
  toInvestmentBadgeView,
  type BadgeView,
  type InvestmentRowActionType,
} from '@app/helpers/status.helper';
import type { InvestmentResponse } from '@app/models/admin.model';
import type { Investment } from '@app/models/investment.models';
import { GenericStore } from '@app/stores/generic.store';
import { BadgeCellRendererComponent } from '@app/ui-components/grid-table/badge-cell-renderer/badge-cell-renderer.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { InvestmentActionCellRendererComponent } from '@app/ui-components/grid-table/investment-action-cell-renderer/investment-action-cell-renderer.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';

type InvestmentRow = {
  id: string;
  sentRaw: string;
  sentDate: string;
  sentTime: string;
  decidedDateRaw?: string | null;
  fundedDateRaw?: string | null;
  completedDateRaw?: string | null;
  cancelledDateRaw?: string | null;
  refusedDateRaw?: string | null;
  pendingUntilRaw?: string | null;
  bidder: string;
  projectName: string;
  statusId: number | null;
  amount: number | null;
  projectCurrency?: string | null;
  actionType: InvestmentRowActionType;
};

@Component({
  selector: 'app-investments-readonly-table',
  standalone: true,
  imports: [CommonModule, GridTableComponent, TranslatePipe],
  templateUrl: './investments-readonly-table.component.html',
  styleUrl: './investments-readonly-table.component.scss',
})
export class InvestmentsReadonlyTableComponent {
  public investments = input<Investment[]>([]);
  public loading = input<boolean>(false);
  public titleKey = input<string>('admin.sideNav.projects.investments');
  public showSearch = input<boolean>(true);
  public actionsEnabled = input<boolean>(false);
  public acceptClicked = output<string>();
  public rejectClicked = output<string>();
  public approveClicked = output<string>();
  protected columns: ColDef<InvestmentRow>[] = [];

  protected readonly rows = computed<InvestmentRow[]>(() =>
    this.investments().map((investment) => this.mapToRow(investment)),
  );

  private readonly genericStore = inject(GenericStore);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.buildColumns();
    effect(() => {
      this.actionsEnabled();
      this.buildColumns();
    });
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'sentDate',
        headerName: this.translate.instant('projectOffers.table.sent'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (p: ICellRendererParams<InvestmentRow>) => ({
          title: p.data?.sentDate ?? '',
          subtitle: p.data?.sentTime ?? '',
          infoTooltipKey: this.buildDateTooltip(p.data),
        }),
        cellStyle: customRendererCellStyle,
        width: 140,
        flex: 0,
        suppressNavigable: true,
        editable: false,
      },
      {
        field: 'bidder',
        headerName: this.translate.instant('projectOffers.table.bidder'),
        minWidth: 220,
      },
      {
        field: 'projectName',
        headerName: this.translate.instant('admin.investments.table.project'),
        minWidth: 220,
      },
      {
        field: 'amount',
        headerName: this.translate.instant('projectOffers.table.amount'),
        valueFormatter: (p: ValueFormatterParams<InvestmentRow>) =>
          formatAmountWithCode(
            p.data?.amount,
            p.data?.projectCurrency,
            this.translate.currentLang,
          ),
        cellClass: 'text-end',
        minWidth: 160,
        flex: 0,
      },
      {
        field: 'statusId',
        colId: 'status',
        headerName: this.translate.instant('projectOffers.table.status'),
        cellRenderer: BadgeCellRendererComponent,
        cellClass: 'ag-no-ellipsis',
        cellStyle: customRendererCellStyle,
        cellRendererParams: (param: ICellRendererParams<InvestmentRow>) => {
          const id = param.data?.statusId ?? null;
          const statuses = this.genericStore.investmentStatusEntities();
          const statusEnt = statuses.find((s) => s.id === id);
          const name = statusEnt?.name?.trim();
          const view: BadgeView = toInvestmentBadgeView(name);
          const descKey = `admin.investments.statusDescriptions.${name ?? ''}`;
          const tooltipText = this.translate.instant(descKey);
          if (tooltipText && tooltipText !== descKey) {
            view.infoTooltipKey = tooltipText;
          }
          return { view };
        },
        width: 220,
        flex: 0,
      },
    ];
    if (this.actionsEnabled()) {
      this.columns.push({
        colId: 'actions',
        headerName: '',
        cellRenderer: InvestmentActionCellRendererComponent,
        cellRendererParams: (p: ICellRendererParams<InvestmentRow>) => ({
          ...p,
          onAccept: (row: InvestmentRow) => this.acceptClicked.emit(row.id),
          onReject: (row: InvestmentRow) => this.rejectClicked.emit(row.id),
          onApprove: (row: InvestmentRow) => this.approveClicked.emit(row.id),
        }),
        width: 140,
        flex: 0,
        cellClass: 'd-flex justify-content-center align-items-center',
        sortable: false,
        filter: false,
      });
    }
  }

  private mapToRow(investment: Investment): InvestmentRow {
    const sentRaw = investment.sentDate ?? '';
    return {
      id: investment.id,
      sentRaw,
      sentDate: formatDate(sentRaw),
      sentTime: formatTime(sentRaw),
      decidedDateRaw: investment.decidedDate ?? null,
      fundedDateRaw: investment.fundedDate ?? null,
      completedDateRaw: investment.completedDate ?? null,
      cancelledDateRaw: investment.cancelledDate ?? null,
      refusedDateRaw: investment.refeusedDate ?? null,
      pendingUntilRaw: investment.pendingUntil ?? null,
      bidder: investment.legalEntityId ?? investment.userId ?? '—',
      projectName: investment.projectName ?? investment.projectId ?? '—',
      statusId: investment.status ?? null,
      amount: investment.amount ?? null,
      projectCurrency: investment.projectCurrency ?? null,
      actionType: this.actionsEnabled()
        ? getActionType(investment as unknown as InvestmentResponse)
        : null,
    };
  }

  private buildDateTooltip(row?: InvestmentRow): string {
    if (!row) return '';
    const make = (labelKey: string, raw?: string | null) => {
      if (!raw) return null;
      const label = this.translate.instant(labelKey);
      return { raw, line: `${label}: ${formatDateTime(raw)}` };
    };
    const items = [
      make('admin.investments.dates.sentDate', row.sentRaw),
      make('admin.investments.dates.pendingUntil', row.pendingUntilRaw),
      make('admin.investments.dates.decidedDate', row.decidedDateRaw),
      make('admin.investments.dates.fundedDate', row.fundedDateRaw),
      make('admin.investments.dates.completedDate', row.completedDateRaw),
      make('admin.investments.dates.cancelledDate', row.cancelledDateRaw),
      make('admin.investments.dates.refusedDate', row.refusedDateRaw),
    ].filter(Boolean) as { raw: string; line: string }[];
    items.sort((a, b) => a.raw.localeCompare(b.raw));
    return items.map((i) => i.line).join('\n');
  }
}
